import { useState } from 'react';
import { Address as ApiAddress } from '@bulthaup/api/db';
import { getProjectAddresses } from '@bulthaup/api/entities/project';
import { Button, Divider, Stack, Tabs, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconDeviceFloppy } from '@tabler/icons-react';

import { AddressFields } from '../../../-components/-address-fields';
import { RouterOutput } from '../../../../../../services/trpc';
import { handleFormError } from '../../../../../../utils/handle-form-error';
import { InstallerProjectDetails } from './-installer-project-details';

type Address = {
  id?: string;
  address1: string;
  address2: string;
  address3: string;
  city: string;
  postalCode: string;
  code1: string;
  code2: string;
  floor: string;
  elevator: string;
};
interface InstallerProjectEditAddressesFormValues {
  homeAddress: Address;
  deliveryAddress: Address;
  installationAddress: Address;
  billingAddress: Address;
}

interface ProjectFormProps {
  project: RouterOutput['projects']['get'];
  onSubmit: (values: InstallerProjectEditAddressesFormValues) => void;
  initialValues?: InstallerProjectEditAddressesFormValues;
}

export function InstallerProjectEditAddressesForm({
  project,
  onSubmit,
  initialValues,
}: ProjectFormProps) {
  const form = useForm<InstallerProjectEditAddressesFormValues>({
    initialValues,
  });

  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (
    values: InstallerProjectEditAddressesFormValues,
  ) => {
    try {
      setIsLoading(true);
      await onSubmit(values);
    } catch (error) {
      handleFormError(form, error);
    } finally {
      setIsLoading(false);
    }
  };

  const disabledFields = [
    'address1',
    'address2',
    'address3',
    'city',
    'postalCode',
  ];

  const computedAddresses = getProjectAddresses({
    isDeliveryAddressOverride: project.isDeliveryAddressOverride,
    isInstallationAddressOverride: project.isInstallationAddressOverride,
    isBillingAddressOverride: project.isBillingAddressOverride,
    homeAddress: form.values.homeAddress as ApiAddress,
    deliveryAddress: form.values.deliveryAddress as ApiAddress,
    installationAddress: form.values.installationAddress as ApiAddress,
    billingAddress: form.values.billingAddress as ApiAddress,
  });

  return (
    <Stack gap="md">
      <InstallerProjectDetails project={project} />
      <Divider />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <Title order={4}>Adresses</Title>
          <Tabs
            defaultValue="homeAddress"
            styles={{ tab: { padding: '8px 12px' } }}
          >
            <Tabs.List>
              <Tabs.Tab value="homeAddress">Domicile</Tabs.Tab>
              <Tabs.Tab value="deliveryAddress">Livraison</Tabs.Tab>
              <Tabs.Tab value="installationAddress">Installation</Tabs.Tab>
              <Tabs.Tab value="billingAddress">Facturation</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="homeAddress" pt="lg">
              <AddressFields
                prefix="homeAddress"
                form={form}
                disabledFields={disabledFields}
              />
            </Tabs.Panel>

            <Tabs.Panel value="deliveryAddress" pt="lg">
              {project.isDeliveryAddressOverride ? (
                <AddressFields
                  prefix="deliveryAddress"
                  form={form}
                  disabledFields={disabledFields}
                />
              ) : (
                <AddressFields
                  prefix="deliveryAddress"
                  form={form}
                  disabled
                  defaultAddress={computedAddresses.deliveryAddress}
                />
              )}
            </Tabs.Panel>

            <Tabs.Panel value="installationAddress" pt="lg">
              {project.isInstallationAddressOverride ? (
                <AddressFields
                  prefix="installationAddress"
                  form={form}
                  disabledFields={disabledFields}
                />
              ) : (
                <AddressFields
                  prefix="installationAddress"
                  form={form}
                  disabled
                  defaultAddress={computedAddresses.installationAddress}
                />
              )}
            </Tabs.Panel>

            <Tabs.Panel value="billingAddress" pt="lg">
              {project.isBillingAddressOverride ? (
                <AddressFields
                  prefix="billingAddress"
                  form={form}
                  disabledFields={disabledFields}
                />
              ) : (
                <AddressFields
                  prefix="billingAddress"
                  form={form}
                  disabled
                  defaultAddress={computedAddresses.billingAddress}
                />
              )}
            </Tabs.Panel>
          </Tabs>
        </Stack>
        <Button
          type="submit"
          loading={isLoading}
          mt="md"
          leftSection={<IconDeviceFloppy size={16} />}
        >
          Enregistrer
        </Button>
      </form>
    </Stack>
  );
}
